import React, {useState} from "react";
import {Stack, Box, Paper, CircularProgress, Typography} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useContainer from "../hooks/useContainer";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import ContainerTypesDropdownComponent from "../common/container-types-dropdown.component";

const CreateContainerComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {isCreatingContainer, createContainer, isFetchingContainerTypes, containerTypes, getContainerTypes} = useContainer();

    const [containerNumberInput, setContainerNumberInput] = useState("");
    const [selectedContainerType, setSelectedContainerType] = useState(null);

    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    const handleContainerTypeChange = (value) => {
        setSelectedContainerType(value);
    }

    const handleTextfieldChange = (e) => {
        setContainerNumberInput(e.target.value);
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/containers");
        } 
    }

    const handleCreateContainer = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const payload = {
                container_number: containerNumberInput,
                size_type: selectedContainerType,
                org_uid: organizationId
            }

            createContainer(payload, orgIdFromUrlForPortEmployee);

        } else {
            const payload = {
                container_number: containerNumberInput,
                size_type: selectedContainerType,
            }

            createContainer(payload);
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Container List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Add Container"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Create new container for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Container Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to create a new container"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Container Number"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <CustomTextField 
                                    placeholder={"Enter container number"}
                                    variant={"outlined"}
                                    value={containerNumberInput}
                                    onChange={(e) => handleTextfieldChange(e)}
                                    fullWidth={true}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isCreatingContainer}
                                    inputProps={{maxLength: 11}}
                                    sx={{
                                    "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: !containerNumberInput ? "red" : undefined,
                                    },
                                    "&::placeholder": {
                                        color: !containerNumberInput ? "red" : undefined,
                                        opacity: 1,
                                    },
                                    },
                                    "& .MuiInputBase-input::placeholder": {
                                    color: !containerNumberInput ? "red" : undefined,
                                    },
                                }}
                                    helperText={!containerNumberInput ? "Container number is required" : false}
                                />    
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Size & Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                                <ContainerTypesDropdownComponent 
                                    value={selectedContainerType}
                                    onChange={handleContainerTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isCreatingContainer}
                                    isFetchingContainerTypes={isFetchingContainerTypes}
                                    containerTypes={containerTypes}
                                    getContainerTypes={getContainerTypes}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: !containerNumberInput ? "red" : undefined,
                                        },
                                        },
                                        "& .MuiSelect-select::placeholder": {
                                        color: !containerNumberInput ? "red" : undefined,
                                        },
                                    }}

                                />      
                                {!selectedContainerType && (<Typography variant="caption" color="red">Size and type is required</Typography>)}
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isCreatingContainer) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Add Container"}
                            startIcon={isCreatingContainer && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (containerNumberInput && selectedContainerType && !isCreatingContainer) ? "100%" : "55%",
                                cursor: (containerNumberInput && selectedContainerType && !isCreatingContainer) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={
                                (containerNumberInput && selectedContainerType && !isCreatingContainer) ? handleCreateContainer : null
                            }
                        />    
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default CreateContainerComponent